import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Check, MapPin, Users, Star, Calendar, CreditCard, Download, CalendarPlus, Share, Mail, Smartphone, Key, Info } from 'lucide-react';

const BookingConfirmed = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Quick action handlers
  const handleDownload = () => {
    // Create receipt content
    const receiptContent = `
      ROAR HOMES - BOOKING RECEIPT
      ================================
      Confirmation Number: ${bookingData.confirmationNumber}
      Property: ${bookingData.property.name}
      Location: ${bookingData.property.location}
      
      Stay Details:
      Check-in: ${bookingData.stay.checkIn} (${bookingData.stay.checkInDay})
      Check-out: ${bookingData.stay.checkOut} (${bookingData.stay.checkOutDay})
      Nights: ${bookingData.stay.totalNights}
      Guests: ${bookingData.stay.guests}
      
      Payment Summary:
      ${bookingData.payment.nightlyRate}: ${bookingData.payment.nightlyTotal}
      Cleaning Fee: ${bookingData.payment.cleaningFee}
      Service Fee: ${bookingData.payment.serviceFee}
      Caution Fee: ${bookingData.payment.cautionFee}
      VAT (7.5%): ${bookingData.payment.vat}
      TOTAL: ${bookingData.payment.total}
      
      Guest Information:
      Name: ${bookingData.guest.name}
      Email: ${bookingData.guest.email}
      Phone: ${bookingData.guest.phone}
      
      Host Information:
      Host: ${bookingData.host.name}
      ${bookingData.host.status}
      
      Thank you for choosing Roar Homes!
    `;
    
    // Create PDF using browser print functionality
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Roar Homes - Booking Receipt</title>
          <style>
            body { font-family: 'Work Sans', sans-serif; padding: 20px; line-height: 1.6; }
            h1 { font-family: 'Poppins', sans-serif; color: #1f2937; margin-bottom: 20px; }
            h2 { font-family: 'Poppins', sans-serif; color: #374151; margin: 20px 0 10px 0; }
            .header { border-bottom: 2px solid #ec4899; padding-bottom: 10px; margin-bottom: 20px; }
            .section { margin: 20px 0; }
            .total { font-weight: bold; font-size: 18px; color: #1f2937; }
            .confirmation { background: #dbeafe; padding: 10px; border-radius: 8px; margin: 20px 0; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ROAR HOMES - BOOKING RECEIPT</h1>
          </div>
          <div class="confirmation">
            <h2>Confirmation Number: ${bookingData.confirmationNumber}</h2>
          </div>
          <div class="section">
            <h2>Property Information</h2>
            <p><strong>${bookingData.property.name}</strong></p>
            <p>${bookingData.property.location}</p>
            <p>${bookingData.property.guests}</p>
          </div>
          <div class="section">
            <h2>Stay Details</h2>
            <p><strong>Check-in:</strong> ${bookingData.stay.checkIn} (${bookingData.stay.checkInDay})</p>
            <p><strong>Check-out:</strong> ${bookingData.stay.checkOut} (${bookingData.stay.checkOutDay})</p>
            <p><strong>Total Nights:</strong> ${bookingData.stay.totalNights}</p>
            <p><strong>Guests:</strong> ${bookingData.stay.guests}</p>
          </div>
          <div class="section">
            <h2>Payment Summary</h2>
            <p>${bookingData.payment.nightlyRate}: ${bookingData.payment.nightlyTotal}</p>
            <p>Cleaning Fee: ${bookingData.payment.cleaningFee}</p>
            <p>Service Fee: ${bookingData.payment.serviceFee}</p>
            <p>Caution Fee: ${bookingData.payment.cautionFee}</p>
            <p>VAT (7.5%): ${bookingData.payment.vat}</p>
            <p class="total">TOTAL: ${bookingData.payment.total}</p>
          </div>
          <div class="section">
            <h2>Guest Information</h2>
            <p><strong>Name:</strong> ${bookingData.guest.name}</p>
            <p><strong>Email:</strong> ${bookingData.guest.email}</p>
            <p><strong>Phone:</strong> ${bookingData.guest.phone}</p>
          </div>
          <div class="section">
            <h2>Host Information</h2>
            <p><strong>Host:</strong> ${bookingData.host.name}</p>
            <p>${bookingData.host.status}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    
    // Wait for content to load, then trigger print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleAddToCalendar = () => {
    // Create calendar event
    const event = {
      title: `Stay at ${bookingData.property.name}`,
      description: `Booking Confirmation: ${bookingData.confirmationNumber}\n\n${bookingData.property.description}\n\nLocation: ${bookingData.property.location}`,
      location: bookingData.property.location,
      start: new Date('2024-03-15T15:00:00'), // 3:00 PM check-in
      end: new Date('2024-03-18T11:00:00'), // 11:00 AM check-out
    };

    // Create Google Calendar URL
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=20240315T150000Z/20240318T110000Z&details=${encodeURIComponent(event.description)}&location=${encodeURIComponent(event.location)}`;
    
    // Open in new tab
    window.open(googleCalendarUrl, '_blank');
  };

  const handleShareBooking = () => {
    const shareData = {
      title: `Roar Homes Booking - ${bookingData.confirmationNumber}`,
      text: `I just booked ${bookingData.property.name} in ${bookingData.property.location} for ${bookingData.stay.totalNights} nights! Confirmation: ${bookingData.confirmationNumber}`,
      url: window.location.href
    };

    // Check if Web Share API is available
    if (navigator.share) {
      navigator.share(shareData)
        .then(() => {
          // Share successful - no action needed
        })
        .catch((error) => {
          // Handle share errors (including cancellation)
          if (error.name !== 'AbortError') {
            // Only show fallback if not a user cancellation
            const shareText = `${shareData.title}\n\n${shareData.text}\n\n${shareData.url}`;
            navigator.clipboard.writeText(shareText).then(() => {
              alert('Booking details copied to clipboard!');
            }).catch(() => {
              alert('Could not share booking details. Please copy manually.');
            });
          }
        });
    } else {
      // Fallback: Copy to clipboard
      const shareText = `${shareData.title}\n\n${shareData.text}\n\n${shareData.url}`;
      navigator.clipboard.writeText(shareText).then(() => {
        alert('Booking details copied to clipboard!');
      }).catch(() => {
        alert('Could not copy to clipboard. Please copy manually.');
      });
    }
  };

  const handleCancelReservation = () => {
    setShowCancelModal(true);
  };

  const confirmCancellation = async () => {
    try {
      const { updateBookingStatus } = require('../api/bookings');
      await updateBookingStatus(id, 'cancelled');
      alert('Booking cancelled successfully');
      setShowCancelModal(false);
      navigate('/my-bookings');
    } catch (err) {
      alert('Failed to cancel booking. Please try again.');
    }
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount);
  };

  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const { getBookingById } = require('../api/bookings');
        const data = await getBookingById(id);
        if (data) {
          const nightlyTotal = parseFloat(data.totalAmount);
          const securityDeposit = parseFloat(data.Property?.securityDeposit || 0);
          const vat = nightlyTotal * 0.075;
          const finalTotal = nightlyTotal + securityDeposit + vat;

          setBookingData({
            confirmationNumber: data.id,
            property: {
              name: data.Property?.name || "Property",
              description: data.Property?.description || "",
              image: (data.Property?.images && data.Property.images.length > 0) ? data.Property.images[0] : "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
              location: data.Property?.city ? `${data.Property.city}, ${data.Property.country || ''}` : data.Property?.address || "Location",
              guests: `${data.numberOfGuests || 2} Guests`,
              rating: data.Property?.rating || "4.9",
              reviews: "127"
            },
            stay: {
              checkIn: new Date(data.checkInDate).toLocaleDateString(),
              checkInDay: "After 3:00 PM",
              checkOut: new Date(data.checkOutDate).toLocaleDateString(),
              checkOutDay: "Before 11:00 AM",
              totalNights: Math.max(1, Math.ceil((new Date(data.checkOutDate) - new Date(data.checkInDate)) / (1000 * 60 * 60 * 24))),
              guests: `${data.numberOfGuests} guests`
            },
            guest: {
              name: data.User ? `${data.User.firstName} ${data.User.lastName}` : (data.guestFirstName ? `${data.guestFirstName} ${data.guestLastName}` : "Guest"),
              email: data.User?.email || data.guestEmail || "",
              phone: data.User?.phone || data.guestPhone || ""
            },
            host: {
              name: "Roar Homes",
              image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
              status: "Superhost",
              rating: "4.95",
              reviews: "234"
            },
            payment: {
              nightlyRate: `${formatCurrency(Math.round(nightlyTotal / Math.max(1, Math.ceil((new Date(data.checkOutDate) - new Date(data.checkInDate)) / (1000 * 60 * 60 * 24)))))} × ${Math.max(1, Math.ceil((new Date(data.checkOutDate) - new Date(data.checkInDate)) / (1000 * 60 * 60 * 24)))} nights`,
              nightlyTotal: formatCurrency(nightlyTotal),
              securityDeposit: formatCurrency(securityDeposit),
              hasSecurityDeposit: securityDeposit > 0,
              vat: formatCurrency(vat),
              total: formatCurrency(finalTotal),
              method: data.paymentStatus === 'paid' ? "Paid" : "Pending"
            }
          });
        }
      } catch (err) {
        console.error('Failed to fetch booking', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-pink-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (!bookingData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-600">Booking not found.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Confirmation Header */}
      <div className="bg-green-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Booking Confirmed!</h1>
          <p className="text-xl text-gray-600 mb-6">Thank you for choosing RoarHomes. Your reservation has been successfully confirmed.</p>
          <div className="bg-white rounded-lg p-4 inline-block shadow-sm">
            <p className="text-sm text-gray-500">Confirmation Number</p>
            <p className="text-2xl font-bold text-blue-600">#{bookingData.confirmationNumber}</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Booking Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Property Information */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Reservation</h2>
                <div className="flex flex-col md:flex-row gap-6">
                  <img 
                    src={bookingData.property.image} 
                    alt={bookingData.property.name} 
                    className="w-full md:w-48 h-[200px] object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{bookingData.property.name}</h3>
                    <p className="text-gray-600 mb-4">{bookingData.property.description}</p>
                    <div className="space-y-2">
                      <div className="flex items-center text-gray-600">
                        <MapPin className="w-5 h-5 text-blue-600 mr-2" />
                        <span>{bookingData.property.location}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Users className="w-5 h-5 text-blue-600 mr-2" />
                        <span>{bookingData.property.guests}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Star className="w-5 h-5 text-yellow-500 mr-2" />
                        <span>{bookingData.property.rating} ({bookingData.property.reviews} reviews)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stay Details */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Stay Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Check-in</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-lg font-semibold text-gray-900">{bookingData.stay.checkIn}</p>
                    <p className="text-gray-600">{bookingData.stay.checkInDay}</p>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Check-out</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-lg font-semibold text-gray-900">{bookingData.stay.checkOut}</p>
                    <p className="text-gray-600">{bookingData.stay.checkOutDay}</p>
                  </div>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total nights</span>
                  <span className="font-semibold text-gray-900">{bookingData.stay.totalNights} nights</span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-gray-600">Guests</span>
                  <span className="font-semibold text-gray-900">{bookingData.stay.guests}</span>
                </div>
              </div>
            </div>

            {/* Guest Information */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Guest Information</h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500">Primary Guest</p>
                  <p className="text-lg font-semibold text-gray-900">{bookingData.guest.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="text-gray-900">{bookingData.guest.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="text-gray-900">{bookingData.guest.phone}</p>
                </div>
              </div>
            </div>

          </div>

          {/* Booking Summary Sidebar */}
          <div className="space-y-6">
            {/* Payment Summary */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Payment Summary</h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">{bookingData.payment.nightlyRate}</span>
                  <span className="text-gray-900">{bookingData.payment.nightlyTotal}</span>
                </div>
                {bookingData.payment.hasSecurityDeposit && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Security Deposit (Refundable)</span>
                    <span className="text-gray-900">{bookingData.payment.securityDeposit}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">VAT (7.5%)</span>
                  <span className="text-gray-900">{bookingData.payment.vat}</span>
                </div>
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between">
                    <span className="text-lg font-semibold text-gray-900">Total Paid</span>
                    <span className="text-lg font-semibold text-gray-900">{bookingData.payment.total}</span>
                  </div>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center">
                  <CreditCard className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Paid with</p>
                    <p className="font-semibold text-gray-900">{bookingData.payment.method}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Quick Actions</h2>
              <div className="space-y-3">
                <button onClick={handleDownload} className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition duration-300 flex items-center justify-center">
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </button>
                <button onClick={handleAddToCalendar} className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition duration-300 flex items-center justify-center">
                  <CalendarPlus className="w-4 h-4 mr-2" />
                  Add to Calendar
                </button>
                <button onClick={handleShareBooking} className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition duration-300 flex items-center justify-center">
                  <Share className="w-4 h-4 mr-2" />
                  Share Booking
                </button>
              </div>
            </div>

            {/* Cancellation Policy */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Cancellation Policy</h2>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <Info className="w-5 h-5 text-yellow-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-yellow-800 font-medium mb-1">Moderate Cancellation</p>
                    <p className="text-sm text-yellow-700">Free cancellation until March 8. Cancel before check-in on March 15 for a partial refund.</p>
                  </div>
                </div>
              </div>
              <button onClick={handleCancelReservation} className="w-full mt-4 text-red-600 py-2 text-sm font-medium hover:bg-red-50 rounded-lg transition duration-300">
                Cancel Reservation
              </button>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="mt-12 bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">What's Next?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Check Your Email</h3>
              <p className="text-gray-600 text-sm">We've sent a confirmation email with all the details and check-in instructions.</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Download Our App</h3>
              <p className="text-gray-600 text-sm">Get the RoarHomes app for easy access to your booking and 24/7 support.</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Key className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Prepare for Check-in</h3>
              <p className="text-gray-600 text-sm">Your host will send check-in instructions 24 hours before your arrival.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Cancellation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Cancel Reservation</h3>
              <button
                onClick={closeCancelModal}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <Info className="w-5 h-5 text-red-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-red-800 font-medium mb-1">Important Cancellation Policy</p>
                    <p className="text-sm text-red-700">
                      Cancellations made within 48 hours of check-in are non-refundable. 
                      Free cancellation until March 8, 2024.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Booking Details</h4>
                <div className="space-y-1 text-sm">
                  <p><strong>Property:</strong> {bookingData.property.name}</p>
                  <p><strong>Confirmation:</strong> #{bookingData.confirmationNumber}</p>
                  <p><strong>Check-in:</strong> {bookingData.stay.checkIn}</p>
                  <p><strong>Check-out:</strong> {bookingData.stay.checkOut}</p>
                  <p><strong>Nights:</strong> {bookingData.stay.totalNights}</p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Refund Amount:</strong> ₦0.00 (Non-refundable within 48 hours)
                </p>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex space-x-3 mt-6">
              <button
                onClick={closeCancelModal}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Keep Reservation
              </button>
              <button
                onClick={confirmCancellation}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Confirm Cancellation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingConfirmed;
